/**
 * Hook to load a QuestionnaireResponse + its referenced Questionnaire for
 * the Document Review page.
 *
 * Matches the Appointment page's linking strategy:
 *   1. Try direct read (documentId is a QR resource ID)
 *   2. Fall back to tag-based search (documentId may be an appointment ID
 *      tagged on the QR via APPOINTMENT_TAG_SYSTEM)
 *
 * Gracefully handles missing resources so the rest of the page still renders.
 */
import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { APPOINTMENT_TAG_SYSTEM } from '../../appointments/fhirNoteMapping';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UseDocumentReviewResponseResult {
  response: QuestionnaireResponse | null;
  questionnaire: Questionnaire | null;
  loading: boolean;
  error: string | null;
  /** Re-fetch the current document (e.g. after removing provider signature on Request Edits). */
  refetch: () => Promise<void>;
  /** Apply a write result locally (e.g. after fhirUpdateQR bypasses the Medplum client cache). */
  setResponse: (qr: QuestionnaireResponse | null) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDocumentReviewResponse(
  documentId: string | undefined,
): UseDocumentReviewResponseResult {
  const medplum = useMedplum();
  const [response, setResponse] = useState<QuestionnaireResponse | null>(null);
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const doFetch = useCallback(async (): Promise<void> => {
    if (!documentId) {
      setResponse(null);
      setQuestionnaire(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    let qr: QuestionnaireResponse | null = null;

    // Raw FHIR writes (fhirUpdateQR) bypass the client cache; invalidate before read.
    medplum.invalidateSearches('QuestionnaireResponse');

    // Strategy 1: direct read (documentId is a QR resource ID)
    try {
      qr = await medplum.readResource('QuestionnaireResponse', documentId);
    } catch {
      // Not a valid QR ID — try tag-based search next
    }

    // Strategy 2: tag-based search (same as Appointment page)
    if (!qr) {
      try {
        const results = await medplum.searchResources('QuestionnaireResponse', {
          _tag: `${APPOINTMENT_TAG_SYSTEM}|${documentId}`,
          _sort: '-_lastUpdated',
          _count: '1',
        });
        if (results.length > 0) {
          qr = results[0];
        }
      } catch {
        // Tag search also failed
      }
    }

    if (!qr) {
      setResponse(null);
      setQuestionnaire(null);
      setError(
        'QuestionnaireResponse not found. The document may not be available in Medplum yet.',
      );
      setLoading(false);
      return;
    }

    setResponse(qr);

    // Resolve the referenced Questionnaire (same pattern as useAppointmentResponses)
    if (qr.questionnaire) {
      try {
        const ref = qr.questionnaire;
        const match = ref.match(/Questionnaire\/([^/]+)$/);
        const qId = match?.[1];
        if (qId) {
          const q = await medplum.readResource('Questionnaire', qId);
          setQuestionnaire(q);
        }
      } catch {
        // Questionnaire not found — graceful degradation (labels may be missing)
        setQuestionnaire(null);
      }
    }

    setLoading(false);
  }, [medplum, documentId]);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  return { response, questionnaire, loading, error, refetch: doFetch, setResponse };
}
